import React, { useEffect, useState } from 'react';
import 'bootstrap-select';
import { useNavigate, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PrincipalService from 'service/PrincipalService';
import LoadingSpinner from 'component/LoadingSpinner';
import { PrincipalCreateReq } from 'domain/payload/request/principal/PrincipalCreateReq';
import { PrincipalUpdateReq } from 'domain/payload/request/principal/PrincipalUpdateReq';

const PrincipalForm: React.FC = () => {
  const { id } = useParams();
  const { profile } = useSelector((state: any) => state.auth);

  const navigate = useNavigate();

  const principalService = PrincipalService();
  const token = profile?.data.token;

  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [personInCharge, setPersonInCharge] = useState('');
  const [principalLogo, setPrincipalLogo] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingSave, setIsLoadingSave] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoadingSave(true);

    try {
      let principalId: string | undefined = id;

      if (id === "0") {
        const result = await principalCreate();
        principalId = result?.data?.id ?? principalId;
        alert(`Data ${name} successfully created!`);
      } else {
        await principalUpdate();
        alert(`Data ${name} successfully updated!`);
      }

      if (principalLogo && principalId && principalId !== "0") {
        if (token) {
          await principalService.uploadLogo(token, principalId, principalLogo);
        }
      }

      navigate("/principal");
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat menyimpan data';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingSave(false);
    }
  };

  const fetchDetail = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingDetail(true);
    try {
      const res = await principalService.principalDetail(token, id ?? "0");
      setName(res.data?.name || '');
      setLegalName(res.data?.legalName || '');
      setAddress(res.data?.address || '');
      setEmail(res.data?.email || '');
      setPhone(res.data?.phone || '');
      setPersonInCharge(res.data?.personInCharge || '');
      setPreviewUrl(res.data?.logoUrl || null);

      setIsLoadingDetail(false);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail principal';
      window.alert(`Error: ${errorMessage}`);
      setIsLoadingDetail(false);
    }
  };

  const principalCreate = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      const request: PrincipalCreateReq = {
        name: name,
        legalName: legalName,
        address: address,
        email: email,
        phone: phone,
        personInCharge: personInCharge
      };

      return await principalService.principalCreate(token, request);
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const principalUpdate = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    try {
      const request: PrincipalUpdateReq = {
        id: id!,
        name: name,
        legalName: legalName,
        address: address,
        email: email,
        phone: phone,
        personInCharge: personInCharge
      };

      return await principalService.principalUpdate(token, request);
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const MAX_SIZE = 100 * 1024;

  const handleFileChange = (file: File | null) => {
    if (!file) {
      setPrincipalLogo(null);
      setPreviewUrl(null);
      return;
    }

    if (file.type !== "image/png") {
      alert("Format gambar harus .png");
      return;
    }
    if (file.size > MAX_SIZE) {
      alert("Ukuran file maksimal 100 KB");
      return;
    }
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      if (img.width !== img.height) {
        alert("Gambar harus rasio 1:1");
        URL.revokeObjectURL(objectUrl);
        return;
      }

      setPrincipalLogo(file);
      setPreviewUrl(objectUrl);
    };

    img.src = objectUrl;
  };

  useEffect(() => {
    if (id !== "0") {
      fetchDetail();
    }
  }, []);

  return (
    <div className="pt-1 h-100" style={{ background: '#f2f4f8' }}>
      <div className="m-4">
        <h4 className="mb-4">{id === "0" ? "Add Principal" : "Edit Principal"}</h4>
        <div className="p-4" style={{ background: 'white' }}>
          {
            isLoadingDetail ?
              <LoadingSpinner />
              :
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-sm-6">
                    <div>
                      <h5 className="mb-4">Basic Information</h5>
                      <div className="mb-3">
                        <label htmlFor="name" className="form-label fw-semibold">Principal Name</label>
                        <input
                          type="text"
                          id="name"
                          className="form-control form-control-compact"
                          value={name}
                          placeholder="Principal Name"
                          onChange={(e) => setName(e.target.value)}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="legalName" className="form-label fw-semibold">Legal Name</label>
                        <input
                          type="text"
                          id="legalName"
                          className="form-control form-control-compact"
                          value={legalName}
                          placeholder="Legal Name"
                          onChange={(e) => setLegalName(e.target.value)}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="address" className="form-label fw-semibold">Address</label>
                        <input
                          type="text"
                          id="address"
                          className="form-control form-control-compact"
                          value={address}
                          placeholder="Address"
                          onChange={(e) => setAddress(e.target.value)}
                          required
                        />
                      </div>
                      <div className='mb-3'>
                        <label className="form-label fw-semibold">Principal Logo</label>
                        <div className='d-flex gap-3'>
                          {previewUrl ?
                            <img src={previewUrl} className='rounded col-auto border' alt="Logo Preview" style={{ width: '120px', height: '120px', objectFit: 'contain' }} />
                            :
                            <span className='rounded col-auto border text-center d-flex align-items-center justify-content-center' style={{ width: '120px', height: '120px' }}>Belum Ada Logo</span>
                          }
                          <div className='col'>
                            <input
                              type="file"
                              className="form-control form-control-compact"
                              accept="image/*"
                              onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-sm-6">
                    <div>
                      <h5 className="mb-4">Contact Info</h5>
                      <div className="mb-3">
                        <label htmlFor="personInCharge" className="form-label fw-semibold">Person In Charge</label>
                        <input
                          type="text"
                          id="personInCharge"
                          className="form-control form-control-compact"
                          value={personInCharge}
                          placeholder="Person In Charge"
                          onChange={(e) => setPersonInCharge(e.target.value)}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label fw-semibold">Email</label>
                        <input
                          type="text"
                          id="email"
                          className="form-control form-control-compact"
                          value={email}
                          placeholder="Email"
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                      </div>
                      <div className="mb-3">
                        <label htmlFor="phone" className="form-label fw-semibold">Phone</label>
                        <input
                          type="text"
                          id="phone"
                          className="form-control form-control-compact"
                          value={phone}
                          placeholder="Phone"
                          onChange={(e) => setPhone(e.target.value)}
                          required
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="w-100 d-flex justify-content-end">
                  <Link to="/principal">
                    <button type="button" className="btn btn-outline-primary">Cancel</button>
                  </Link>
                  <button type="submit" className="btn btn-primary d-flex justify-content-center align-items-center ms-2" disabled={isLoadingSave}>
                    {
                      isLoadingSave &&
                      <>
                        <LoadingSpinner reverse={true} />
                        <p className="ms-2"></p>
                      </>
                    }
                    Save
                  </button>
                </div>
              </form>
          }
        </div>
      </div>
    </div>
  );
};

export default PrincipalForm;
